import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";
import { SectionHeading, LuxButton, revealProps } from "@/components/lux";

const TYPES = ["General Inquiry", "Book a Shoot", "Request a Quote"];

export default function ContactSection() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "", inquiry_type: "General Inquiry" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) { toast.error("Please fill in your name, email and message."); return; }
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent! I'll be in touch shortly.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "", inquiry_type: "General Inquiry" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Try again.");
    } finally { setLoading(false); }
  };

  return (
    <section id="contact" className="relative py-24 sm:py-32 bg-[#090A0F]" data-testid="contact-section">
      <div className="mx-auto max-w-[1300px] px-5 sm:px-8 grid lg:grid-cols-2 gap-14">
        <motion.div {...revealProps()}>
          <SectionHeading eyebrow="Get In Touch" title="Let's Create Something Bold"
            subtitle="Whether it's a shoot, a quote or a question — reach out and let's talk." />
          <div className="mt-10 space-y-3">
            {[["BOOK A SHOOT", "Ready to lock in a session", () => navigate("/book")],
              ["REQUEST A QUOTE", "For custom & larger projects", () => navigate("/book?type=quote")]].map(([t, d, fn]) => (
              <button key={t} onClick={fn} data-testid={`contact-cta-${t.split(" ")[0].toLowerCase()}`}
                className="group w-full text-left flex items-center justify-between rounded-xl border border-white/10 bg-[#12141C] px-6 py-5 hover:border-[#02AACF]/60 transition-all">
                <div>
                  <p className="font-display font-bold text-white group-hover:text-[#02AACF] transition-colors">{t}</p>
                  <p className="text-xs text-[#64748B] mt-0.5">{d}</p>
                </div>
                <span className="text-[#02AACF] text-xl">→</span>
              </button>
            ))}
          </div>
        </motion.div>

        <motion.form {...revealProps(0.15)} onSubmit={submit} className="rounded-2xl bg-[#12141C] border border-white/10 p-7 sm:p-8 space-y-4" data-testid="contact-form">
          <div className="flex gap-2 flex-wrap">
            {TYPES.map((t) => (
              <button key={t} type="button" onClick={() => setForm({ ...form, inquiry_type: t })}
                className={`text-xs font-mono2 px-3 py-2 rounded-full border transition-all ${form.inquiry_type === t ? "bg-[#02AACF] border-[#02AACF] text-white" : "border-white/15 text-white/60"}`}
                data-testid={`contact-type-${t.split(" ")[0].toLowerCase()}`}>
                {t}
              </button>
            ))}
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Name" v={form.name} onChange={set("name")} testid="contact-name" />
            <Field label="Email" type="email" v={form.email} onChange={set("email")} testid="contact-email" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Phone" v={form.phone} onChange={set("phone")} testid="contact-phone" />
            <Field label="Subject" v={form.subject} onChange={set("subject")} testid="contact-subject" />
          </div>
          <div>
            <Label className="text-[#94A3B8] text-xs font-mono2 tracking-wide">Message</Label>
            <Textarea value={form.message} onChange={set("message")} rows={4} data-testid="contact-message"
              className="mt-2 bg-[#0b0d12] border-white/10 text-white focus-visible:ring-[#02AACF]" />
          </div>
          <LuxButton type="submit" disabled={loading} className="w-full" data-testid="contact-submit">
            {loading ? "Sending…" : "SEND MESSAGE"}
          </LuxButton>
        </motion.form>
      </div>
    </section>
  );
}

function Field({ label, v, onChange, type = "text", testid }) {
  return (
    <div>
      <Label className="text-[#94A3B8] text-xs font-mono2 tracking-wide">{label}</Label>
      <Input type={type} value={v} onChange={onChange} data-testid={testid}
        className="mt-2 bg-[#0b0d12] border-white/10 text-white focus-visible:ring-[#02AACF]" />
    </div>
  );
}
